import { type HTMLAttributes, forwardRef, type ReactNode, useState } from 'react'
import { Card, CardContent } from '@/components/composite/Card'
import { RetentionHeader } from '@/components/composite/RetentionHeader'
import { RewardCard } from '@/components/composite/RewardCard'
import { ItemDetailPopover } from '@/components/composite/ItemDetailPopover'
import { EmptySlot } from '@/components/base/EmptySlot'
import { CloseButton } from '@/components/base/CloseButton'
import { BackpackIcon } from '@/icons'
import type { InventoryItemData } from './types'
import { useInventoryGrid, getGridItemInfo } from './hooks/useInventoryGrid'
import { gridItemVariants } from './InventoryPanel.variants'

export interface InventoryPanelProps extends HTMLAttributes<HTMLDivElement> {
  items: InventoryItemData[]
  title: string
  subtitle?: ReactNode
  tooltip?: ReactNode
  onClose?: () => void
}

export const InventoryPanel = forwardRef<HTMLDivElement, InventoryPanelProps>(
  ({ items, title, subtitle, tooltip, onClose, ...props }, ref) => {
    const accentColor = 'yellow'
    const [selectedItem, setSelectedItem] = useState<InventoryItemData | null>(null)
    const { columnsCount, filledSlots, gridTemplateColumns, contentRef } = useInventoryGrid(items)

    const handleItemClick = (item: InventoryItemData) => {
      setSelectedItem(item)
      item.onClick?.()
    }

    return (
      <Card 
        accentColor={accentColor} 
        className="h-full [&>div]:h-full [&>div]:p-4 bg-primary-background" 
        ref={ref} 
        {...props}
      >
        {onClose ? (
          <CloseButton 
            className="absolute top-2 right-2 z-10"
            onClick={onClose}
            size="lg"
          />
        ) : null}

        <div className="flex flex-col h-full">
          <RetentionHeader
            accentColor={accentColor}
            icon={<BackpackIcon className="w-[22px] h-[22px]" />}
            subtitle={subtitle}
            title={title}
            tooltip={tooltip}
          />

          <CardContent className="mt-4 flex-1 overflow-hidden">
            <div 
              className="grid max-h-40 overflow-y-auto overflow-x-hidden bg-[#252525] rounded-xl border border-white/20 justify-center gap-0" 
              ref={contentRef} 
              style={{ gridTemplateColumns, gridAutoRows: 'min-content' }}
            >
              {filledSlots.map((item, index) => {
                const { isLastColumn, isLastRow } = getGridItemInfo(index, columnsCount, filledSlots.length)

                if (item === null) {
                  return (
                    <div
                      className={gridItemVariants({
                        hasRightBorder: !isLastColumn,
                        hasBottomBorder: !isLastRow
                      })}
                      key={`empty-slot-${Math.floor(index / columnsCount)}-${index % columnsCount}`}
                    >
                      <EmptySlot height={82} width={72} />
                    </div>
                  )
                }
                
                return (
                  <div 
                    className={gridItemVariants({
                      hasRightBorder: !isLastColumn,
                      hasBottomBorder: !isLastRow,
                      isGroup: true
                    })}
                    key={item.id}
                  >
                    <ItemDetailPopover
                      accentColor={item.accentColor}
                      description={item.description}
                      footer={item.footer}
                      icon={item.icon}
                      onOpenChange={(open) => {
                        if (open) {
                          handleItemClick(item)
                        } else {
                          setSelectedItem(null)
                        }
                      }}
                      open={selectedItem?.id === item.id}
                      subtitle={item.subtitle}
                      title={`${item.quantity}`}
                    >
                      <RewardCard
                        accentColor={item.accentColor}
                        className="cursor-pointer hover:scale-105 transition-transform !w-[72px] !h-[82px]"
                        icon={item.icon}
                        label={item.name}
                        reward={item.quantity.toString()}
                      />
                    </ItemDetailPopover>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </div>
      </Card>
    )
  }
)

InventoryPanel.displayName = 'InventoryPanel'